import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import StreetviewIcon from "@mui/icons-material/Streetview";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import StreetViewDialog from "./streetViewDialog/StreetViewDialog";

/**
 * Top-right action cluster for an assessment card: an always-visible kebab
 * button opening a dropdown of the card's actions. Owns the Street View dialog
 * and the menu anchor so the cards stay DRY.
 *
 * `onEdit` is optional — when omitted, the Edit action is not rendered.
 */
function AssessmentCardActions({
  latitude,
  longitude,
  onDownload,
  isDownloadingPdf,
  onEdit,
  showStreetView = true,
}) {
  const { t } = useTranslation();
  const [streetViewOpen, setStreetViewOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const openStreetView = (event) => {
    event.stopPropagation();
    setStreetViewOpen(true);
  };

  const handleDownload = (event) => {
    event.stopPropagation();
    if (!isDownloadingPdf && onDownload) onDownload();
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    if (onEdit) onEdit();
  };

  const openMenu = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = (event) => {
    event?.stopPropagation?.();
    setMenuAnchor(null);
  };

  const runFromMenu = (handler) => (event) => {
    handler(event);
    setMenuAnchor(null);
  };

  const downloadTitle = isDownloadingPdf
    ? t("risksMap.pdf.exporting")
    : t("risksMap.pdf.download");

  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 2,
          display: "flex",
          gap: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={openMenu}
          aria-label={t("common.menu")}
          sx={{ p: 0.5 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {showStreetView && (
            <MenuItem onClick={runFromMenu(openStreetView)} dense>
              <ListItemIcon>
                <StreetviewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("common.viewStreetView")}</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={runFromMenu(handleDownload)}
            disabled={isDownloadingPdf}
            dense
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{downloadTitle}</ListItemText>
          </MenuItem>
          {onEdit && (
            <MenuItem onClick={runFromMenu(handleEdit)} dense>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t("common.edit")}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>

      {showStreetView && (
        <StreetViewDialog
          open={streetViewOpen}
          onClose={() => setStreetViewOpen(false)}
          latitude={latitude}
          longitude={longitude}
        />
      )}
    </>
  );
}

export default AssessmentCardActions;
